import { useMemo, useState } from 'react'
import { CheckCircle2, Loader2, ShieldCheck, X } from 'lucide-react'
import i18n from '../i18n'
import { INDIAN_STATES, getDistricts } from '../data/indianStates'
import { OATH_TEXT, OATH_LANG_LABELS, type OathLang } from '../data/oathText'
import { submitOath, checkOathExists, type OathPayload } from '../services/oathService'

// localStorage key — once the visitor has taken (or been verified as having
// taken) the pledge, the curtain + oath never replay on this browser. Shared
// with App.tsx so the curtain gate and the oath gate agree.
export const OATH_TAKEN_KEY = 'tbmukt_oath_taken'

// Devanagari-capable font stack so Hindi/Marathi pledge text renders cleanly
// (no broken/box glyphs) independent of the app's base font.
const DEVANAGARI_FONT =
  "'Noto Sans Devanagari','Nirmala UI','Mangal','Segoe UI',system-ui,-apple-system,sans-serif"

type Mode = 'new' | 'verify'

function detectLang(): OathLang {
  const l = (i18n.language || 'en').split('-')[0]
  return l === 'hi' ? 'hi' : l === 'mr' ? 'mr' : 'en'
}

function validEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
}
function validMobile(v: string) {
  return /^[6-9]\d{9}$/.test(v)
}

/**
 * TB-elimination oath/pledge overlay — shown immediately after the inauguration
 * curtain opens. The visitor MUST either take the pledge (fill the form + tick
 * the checkbox) or, if they've already taken it on another device/session,
 * verify by email or mobile. On either success path the gate is written to
 * localStorage and `onAccept` fires so the app becomes visible.
 *
 * Rendered as a `fixed inset-0 z-[9999]` overlay (same layer as the curtain).
 * Mandatory: there is no skip/close — the only ways out are the two success
 * paths (per product owner).
 */
export default function OathPledge({ onAccept }: { onAccept: () => void }) {
  const [lang, setLang] = useState<OathLang>(detectLang)
  const content = OATH_TEXT[lang]
  const isDeva = lang !== 'en'

  const [mode, setMode] = useState<Mode>('new')

  // New-pledge form
  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [surname, setSurname] = useState('')
  const [email, setEmail] = useState('')
  const [mobile, setMobile] = useState('')
  const [state, setState] = useState('')
  const [district, setDistrict] = useState('')
  const [address, setAddress] = useState('')
  const [accepted, setAccepted] = useState(false)

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Verify form
  const [verifyEmail, setVerifyEmail] = useState('')
  const [verifyMobile, setVerifyMobile] = useState('')
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [verifying, setVerifying] = useState(false)

  const districts = useMemo(() => getDistricts(state), [state])

  function finish() {
    try {
      localStorage.setItem(OATH_TAKEN_KEY, '1')
    } catch {
      /* storage unavailable (private mode) — proceed anyway */
    }
    onAccept()
  }

  function validate(): boolean {
    const e: Record<string, string> = {}
    if (!firstName.trim()) e.firstName = 'Required'
    if (!surname.trim()) e.surname = 'Required'
    if (!email.trim()) e.email = 'Required'
    else if (!validEmail(email.trim())) e.email = 'Enter a valid email'
    if (!mobile.trim()) e.mobile = 'Required'
    else if (!validMobile(mobile.trim())) e.mobile = 'Enter a valid 10-digit mobile'
    if (!state) e.state = 'Required'
    if (!district) e.district = 'Required'
    if (!accepted) e.accepted = 'Please accept the pledge to continue'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(ev: React.FormEvent) {
    ev.preventDefault()
    setSubmitError(null)
    if (!validate()) return
    setSubmitting(true)
    try {
      const payload: OathPayload = {
        firstName: firstName.trim(),
        middleName: middleName.trim() || undefined,
        surname: surname.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        state,
        district,
        address: address.trim() || undefined,
        language: lang,
        accepted: true,
      }
      await submitOath(payload)
      finish()
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ||
          'Could not submit the pledge. Please check your details and try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  async function handleVerify(ev: React.FormEvent) {
    ev.preventDefault()
    setVerifyError(null)
    if (!verifyEmail.trim() && !verifyMobile.trim()) {
      setVerifyError('Enter the email or mobile you pledged with.')
      return
    }
    setVerifying(true)
    try {
      const exists = await checkOathExists({
        email: verifyEmail.trim() || undefined,
        mobile: verifyMobile.trim() || undefined,
      })
      if (exists) {
        finish()
      } else {
        setVerifyError('No pledge found for that email or mobile. Please take the pledge below.')
        setMode('new')
      }
    } catch {
      setVerifyError('Could not verify right now. Please take the pledge below to continue.')
      setMode('new')
    } finally {
      setVerifying(false)
    }
  }

  const inputCls =
    'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1'

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto bg-slate-900/80 backdrop-blur-sm">
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden my-auto">
          {/* Header band */}
          <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-700 px-5 sm:px-7 py-4 text-white">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-6 h-6" />
                <span className="font-semibold text-sm sm:text-base">TB Mukt Bharat — Pledge</span>
              </div>
              {/* Language toggle */}
              <div className="flex items-center gap-1 bg-white/15 rounded-full p-0.5">
                {OATH_LANG_LABELS.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setLang(l.code)}
                    className={`px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium transition ${
                      lang === l.code ? 'bg-white text-blue-700' : 'text-white/90 hover:bg-white/10'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="px-5 sm:px-7 py-5 max-h-[calc(100vh-180px)] overflow-y-auto">
            {/* Pledge text */}
            <div
              lang={content.lang}
              style={isDeva ? { fontFamily: DEVANAGARI_FONT } : undefined}
              className="rounded-xl bg-blue-50/60 border border-blue-100 p-4 sm:p-5"
            >
              <h2 className="text-lg sm:text-xl font-bold text-blue-900 leading-snug">
                {content.title}
              </h2>
              <div className="mt-3 space-y-2 text-sm sm:text-[15px] text-gray-800 leading-relaxed">
                {content.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>

            {/* Mode switch */}
            <div className="mt-4 flex items-center gap-4 text-xs">
              <button
                type="button"
                onClick={() => setMode('new')}
                className={`font-medium ${mode === 'new' ? 'text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Take the pledge
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() => setMode('verify')}
                className={`font-medium ${mode === 'verify' ? 'text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Already taken the pledge? Verify
              </button>
            </div>

            {mode === 'verify' && (
              <form onSubmit={handleVerify} className="mt-3 rounded-xl border border-gray-200 p-4 bg-gray-50/60">
                <p className="text-xs text-gray-600 mb-3">
                  Enter the email or mobile you used last time — we’ll confirm your pledge and let you in.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Email</label>
                    <input
                      type="email"
                      value={verifyEmail}
                      onChange={(e) => setVerifyEmail(e.target.value)}
                      className={inputCls}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Mobile</label>
                    <input
                      type="tel"
                      value={verifyMobile}
                      onChange={(e) => setVerifyMobile(e.target.value)}
                      className={inputCls}
                      placeholder="10-digit mobile"
                    />
                  </div>
                </div>
                {verifyError && <p className="mt-2 text-xs text-red-600">{verifyError}</p>}
                <button
                  type="submit"
                  disabled={verifying}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
                  Verify & continue
                </button>
              </form>
            )}

            {mode === 'new' && (
              <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelCls}>First name *</label>
                    <input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={inputCls}
                    />
                    {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Middle name</label>
                    <input
                      value={middleName}
                      onChange={(e) => setMiddleName(e.target.value)}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Surname *</label>
                    <input
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      className={inputCls}
                    />
                    {errors.surname && <p className="mt-1 text-xs text-red-600">{errors.surname}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={inputCls}
                      placeholder="you@example.com"
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Mobile no. *</label>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      className={inputCls}
                      placeholder="10-digit mobile"
                      maxLength={10}
                    />
                    {errors.mobile && <p className="mt-1 text-xs text-red-600">{errors.mobile}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>State *</label>
                    <select
                      value={state}
                      onChange={(e) => {
                        setState(e.target.value)
                        setDistrict('')
                      }}
                      className={inputCls}
                    >
                      <option value="">Select state</option>
                      {INDIAN_STATES.map((s) => (
                        <option key={s.name} value={s.name}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    {errors.state && <p className="mt-1 text-xs text-red-600">{errors.state}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>District *</label>
                    <select
                      value={district}
                      onChange={(e) => setDistrict(e.target.value)}
                      className={inputCls}
                      disabled={!state}
                    >
                      <option value="">{state ? 'Select district' : 'Select state first'}</option>
                      {districts.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    {errors.district && <p className="mt-1 text-xs text-red-600">{errors.district}</p>}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Address (optional)</label>
                  <textarea
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className={inputCls}
                    rows={2}
                    placeholder="House / street / area (optional)"
                  />
                </div>

                {/* Accept checkbox — pledge label in the selected language */}
                <label
                  lang={content.lang}
                  style={isDeva ? { fontFamily: DEVANAGARI_FONT } : undefined}
                  className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg bg-amber-50 border border-amber-200 p-3"
                >
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-800">{content.checkboxLabel}</span>
                </label>
                {errors.accepted && <p className="-mt-2 text-xs text-red-600">{errors.accepted}</p>}

                {submitError && (
                  <p className="text-sm text-red-600 rounded-lg bg-red-50 border border-red-100 p-2.5">
                    {submitError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-700 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:opacity-95 disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4" /> I accept & take the pledge</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}